import React, { ChangeEvent, useCallback, useContext, useEffect } from 'react';
import { TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { isEmpty, isNil } from 'lodash';
import { ValuationApproachGpcCompGroup, ValuationApproachPtCompGroup } from 'api';
import CompGroupDialogContext from 'pages/Valuations/components/CompGroups/components/common/CompGroupDialogContext';
import FormLabelCompGroup from 'pages/Valuations/components/CompGroups/components/common/FormLabelCompGroup';
import { CompGroupCompany } from '../common/types';

const AutomaticUpdates = () => {
  const options = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  const {
    currentCompGroups,
    compGroupSelected,
    setCompGroupSelected,
    setCompaniesToDelete,
    setCompGroupToDelete,
    setValid,
    isGPC,
  } = useContext(CompGroupDialogContext);

  const resetCompGroupInfo = useCallback(
    (useLatestVersion = false, newCompanies?: CompGroupCompany[] | null) => {
      setCompGroupSelected(currentState => ({
        ...currentState,
        use_latest_comp_group_version: useLatestVersion,
        companies: newCompanies || currentState.companies,
      }));
    },
    [setCompGroupSelected]
  );

  const getMatchedCompGroup = (
    data: ValuationApproachPtCompGroup[] | ValuationApproachGpcCompGroup[],
    optionId: number
  ) => data.find(item => item.comp_group.toString() === optionId.toString());

  const handleChange = (__event: ChangeEvent<object> | null, selectedOption: { value: boolean }) => {
    const useLatestVersion = selectedOption.value;
    const { originalData } = compGroupSelected;
    let newCompaniesData: CompGroupCompany[] | null = null;
    if (useLatestVersion) {
      newCompaniesData = isGPC
        ? (originalData?.gpc_comps as CompGroupCompany[])
        : (originalData?.pt_comps as CompGroupCompany[]);
      setValid(true);
      setCompaniesToDelete([]);
      setCompGroupToDelete(null);
    } else {
      const matchedCompGroup = getMatchedCompGroup(currentCompGroups, compGroupSelected.id);
      if (matchedCompGroup) {
        setCompGroupToDelete(matchedCompGroup.comp_group);
      }
    }
    resetCompGroupInfo(useLatestVersion, newCompaniesData);
  };

  useEffect(() => {
    resetCompGroupInfo(null);
    if (compGroupSelected?.id) {
      if (!isEmpty(currentCompGroups)) {
        const matchedCompGroup = getMatchedCompGroup(currentCompGroups, compGroupSelected.id);
        if (matchedCompGroup) {
          const useLatestVersion = matchedCompGroup.use_latest_comp_group_version;
          resetCompGroupInfo(useLatestVersion);
        } else {
          resetCompGroupInfo(false);
        }
      } else {
        resetCompGroupInfo(false);
      }
    } else {
      setValid(false);
    }
  }, [compGroupSelected.id, currentCompGroups, setValid, resetCompGroupInfo]);

  const getAutomaticUpdatesValue = () => {
    const useLatestVersion = compGroupSelected?.use_latest_comp_group_version;
    if (!isNil(useLatestVersion)) {
      return options.find(item => item.value === useLatestVersion);
    }
    return useLatestVersion;
  };

  return (
    <>
      <FormLabelCompGroup title="Automatic Updates:" />
      <Autocomplete
        id="comp-groups-autocomplete"
        disabled={isNil(compGroupSelected?.id)}
        options={options}
        getOptionLabel={option => option.label || ''}
        getOptionSelected={(option, current) => option.value === current.value}
        renderInput={params => <TextField {...params} placeholder="Select" variant="outlined" />}
        onChange={handleChange}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        value={getAutomaticUpdatesValue() || null}
        disableClearable
      />
    </>
  );
};

export default AutomaticUpdates;
